import {Menu} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {GeofenceSiteGroupEntity} from "@ots-frontend/ots-data";
import {useCallback, useMemo} from "react";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {IconEdit, IconTrash} from "@tabler/icons-react";
import {useGeofenceSiteGroupAction} from "./useGeofenceSiteGroupAction";

const ALL_GEOFENCE_SITE_GROUP_ACTIONS = [
  "EDIT",
  "DELETE",
] as const;

export type GeofenceSiteGroupAction = typeof ALL_GEOFENCE_SITE_GROUP_ACTIONS[number];

export type GeofenceSiteGroupActionMenuDropdownProps = {
  geofenceSiteGroup: GeofenceSiteGroupEntity
  actions?: GeofenceSiteGroupAction[]
  onActionClick?: (action: GeofenceSiteGroupAction) => void
  showEmpty?: boolean
}

export const GeofenceSiteGroupActionMenuDropdown = (props: GeofenceSiteGroupActionMenuDropdownProps) => {
  const {
    geofenceSiteGroup,
    actions = ALL_GEOFENCE_SITE_GROUP_ACTIONS,
    onActionClick,
    showEmpty = true
  } = props

  const {hasAccess} = useAuth()
  const {t} = useTranslation(["common"])

  const visibleActions: Set<GeofenceSiteGroupAction> = useMemo(() => {
    const enabledActions = new Set<GeofenceSiteGroupAction>()
    const actionsSet = actions ? new Set<GeofenceSiteGroupAction>(actions) : new Set<GeofenceSiteGroupAction>()

    if (hasAccess("geofencesitegroup_edit")) {
      if (actionsSet.has("EDIT")) {
        enabledActions.add("EDIT")
      }
    }

    if (hasAccess("geofencesitegroup_delete")) {
      if (actionsSet.has("DELETE")) {
        enabledActions.add("DELETE")
      }
    }

    return enabledActions
  }, [geofenceSiteGroup, actions, hasAccess])

  const {
    onGeofenceSiteGroupActionClick
  } = useGeofenceSiteGroupAction()

  const _onActionClick = useCallback((action: GeofenceSiteGroupAction) => {
    if (onActionClick) {
      onActionClick(action)
    } else {
      onGeofenceSiteGroupActionClick(geofenceSiteGroup, action)
    }
  }, [onActionClick, onGeofenceSiteGroupActionClick, geofenceSiteGroup])

  return (
    <>
      {(showEmpty && visibleActions.size === 0) &&
        <Menu.Item>
          {t("common.action.none", "No Actions")}
        </Menu.Item>
      }
      {visibleActions.has("EDIT") &&
        <Menu.Item icon={<IconEdit size={14}/>} onClick={() => _onActionClick("EDIT")}>
          {t("common.action.edit", "Edit")}
        </Menu.Item>
      }
      {visibleActions.has("DELETE") &&
        <>
          {visibleActions.size > 1 && <Menu.Divider/>}
          <Menu.Item
            onClick={() => _onActionClick("DELETE")}
            icon={<IconTrash size={14}/>}
            color="red">
            {t("common.action.delete", "Delete")}
          </Menu.Item>
        </>
      }
    </>
  )
}
